// Per-stage left-rail accent (SectionPanel's `accentColor`) for the four operational-loop
// panels — same border-l-4 + inline-color technique as AccentRailDisclosure, applied to a
// static (non-collapsible) panel instead. Ulkoiset reuses its own price-line blue; the other
// three match the reference mock's amber/purple/emerald.
export const LOOP_STAGE_ACCENT = {
  environment: "#3b82f6",
  demand: "#fbbf24",
  recipe: "#a855f7",
  impact: "#10b981",
} as const;

export const CHART_MARGIN = { top: 8, right: 8, left: 8, bottom: 0 };

// Same left + right gutter on every loop chart. `YAxis hide` drops the width and
// shifts the plot (and NYT) — Ulkoiset/Resepti hide the unused side, Sisäiset
// only draws the left, so the same 48 h key lands at a different pixel.
export const LOOP_Y_AXIS_WIDTH = 36;

// The shared cursor: every loop chart reports the hovered step index (Recharts'
// `activeTooltipIndex` on the category axis keyed by step) so the "Aktiivinen resepti" card
// can follow it — verification is read at an instant on the timeline, not from a list.
export type HoverHandler = (index: number | null) => void;

export function hoverHandlers(onHover?: HoverHandler) {
  return {
    onMouseMove: (state: { activeTooltipIndex?: number }) =>
      onHover?.(
        typeof state?.activeTooltipIndex === "number"
          ? state.activeTooltipIndex
          : null,
      ),
    onMouseLeave: () => onHover?.(null),
  };
}

export const axisTick = { fontSize: 10, fill: "var(--muted-foreground)" };

// The axis is keyed on the unique step index (see `key` on OperationalLoopPoint), not the
// wrapped "HH:MM" string, so this looks up the display label by that key instead of echoing
// the (numeric) axis value back. Looked up by the tick's *value* (the step key), never by
// Recharts' second formatter argument — that one is the tick's position among the ticks it
// chose to draw, which drifts from the row index as soon as ticks are thinned, and had every
// chart labelling a 48 h window with its first 12 hours.
export function hourTick(
  value: number | string,
  rows: { key: number; isHourMark: boolean; time: string }[],
) {
  // A category axis hands the tick value back as a string even for a numeric dataKey.
  const key = Number(value);
  const row =
    rows[key]?.key === key ? rows[key] : rows.find((r) => r.key === key);
  if (!row) return "";
  return row.isHourMark && wallClockHour(row.time) % 2 === 0 ? row.time : "";
}

// Even wall-clock hours only. The numeric domain still covers the first and last
// 15-min step (Signaalit #1579); labelling those off-hour ends next to 10:00
// stacked "09.15 10.00" on the same pixel.
function wallClockHour(time: string): number {
  return Number(time.split(/[.:]/)[0]);
}

export function hourTickKeys(
  rows: { key: number; isHourMark: boolean; time: string }[],
): number[] {
  return rows
    .filter((r) => r.isHourMark && wallClockHour(r.time) % 2 === 0)
    .map((r) => r.key);
}

/** Pin every loop chart to the shared 15-min grid, not to whichever series has
 *  samples. Sparse KPI / recipe-output lines would otherwise let Recharts shrink
 *  a numeric X domain to dataMin…dataMax (Signaalit #1579). Domain callbacks
 *  ignore Recharts' dataMin/dataMax; a static `[first, last]` tuple is not
 *  enough when one chart's lines are sparse (Optimointiresepti). */
export function loopXAxisProps(
  rows: { key: number; isHourMark: boolean; time: string }[],
) {
  const first = rows[0]?.key ?? 0;
  const last = rows[rows.length - 1]?.key ?? first;
  return {
    dataKey: "key" as const,
    type: "number" as const,
    domain: [() => first, () => last] as [() => number, () => number],
    ticks: hourTickKeys(rows),
    interval: 0 as const,
    tickFormatter: (value: number | string) => hourTick(value, rows),
    tick: axisTick,
    axisLine: false,
    tickLine: false,
    minTickGap: 0,
    allowDataOverflow: false,
    padding: { left: 0, right: 0 },
  };
}

type LoopYDomain = number | string | ((dataMin: number) => number);

/** Always-on left or right Y-axis. Never `hide` — that drops the gutter. An
 *  unused side stays as an unlabeled spacer so every loop plot is the same box. */
export function loopYAxis(
  side: "left" | "right",
  yAxisId: string,
  options?: {
    labeled?: boolean;
    domain?: [LoopYDomain, LoopYDomain];
    ticks?: number[];
  },
) {
  const labeled = options?.labeled !== false;
  return {
    yAxisId,
    orientation: side,
    width: LOOP_Y_AXIS_WIDTH,
    axisLine: false as const,
    tickLine: false as const,
    tick: labeled ? axisTick : false,
    hide: false,
    ...(options?.domain ? { domain: options.domain } : {}),
    ...(options?.ticks ? { ticks: options.ticks } : {}),
  };
}
